// caseDetails.ts
// Service functions for interacting with case details and authorization APIs.
// Provides CRUD operations for procedures, authorization status, and bulk updates.

import { tokenAxios } from '@/utils/axios';
import { Procedure, GetCaseDetailsArgs, UpdateCaseDetailsArgs, UpdateAuthStatusArgs, UpdateAuthStatusBulkArgs } from '@/types/procedure';
import { GetAuthorizationResponse } from '@/types/procedure';

/**
 * Fetches case details for a given practice and case ID.
 * @param params - Parameters including practiceId and caseId
 * @returns The procedure details
 */
export const getCaseDetails = async (params: GetCaseDetailsArgs) => {
  const { data } = await tokenAxios().get<Procedure>(`/api/procedures/${params.practiceId}/${params.caseId}`, {
    params,
  });
  return data;
};

/**
 * Updates case details for a given practice and case ID.
 * @param params - Update parameters including body, user, departmentId, etc.
 * @returns The updated procedure details
 */
export const updateCaseDetails = async (params: UpdateCaseDetailsArgs) => {
  const { data } = await tokenAxios().put<Procedure>(`/api/procedures/${params.practiceId}/${params.caseId}`,
    params.body,
    {
      params: {
        user: params.user,
        departmentId: params.departmentId,
        practiceId: params.practiceId,
        caseId: params.caseId,
      },
    },
  );
  return data;
};

/**
 * Updates the authorization status for a procedure.
 * @param params - Update parameters including body and identifiers
 * @returns The updated procedure details
 */
export const updateAuthStatus = async (params: UpdateAuthStatusArgs) => {
  const { data } = await tokenAxios().post<Procedure>(`/api/procedures/authStatus`,
    params.body,
    {
      params,
    },
  );
  return data;
};

/**
 * Bulk updates authorization status for multiple procedures.
 * @param params - Bulk update parameters including body and identifiers
 * @returns The updated procedure details
 */
export const updateBulkAuthStatus = async (params: UpdateAuthStatusBulkArgs) => {
  const { data } = await tokenAxios().post<Procedure>(`/api/procedures/authStatus/bulk`,
    params.body,
    {
      params,
    },
  );
  return data;
};

/**
 * Fetches the authorization information for a procedure.
 * @param params - Parameters including practiceId and caseId
 * @returns The authorization response
 */
export const getAuthorization = async (params: GetCaseDetailsArgs) => {
  const { data } = await tokenAxios().get<GetAuthorizationResponse>(`/api/procedures/auth`, {
    params,
  });
  return data;
};
